/**
 * Minimal ESM Facebook Login component.
 *
 * Replaces @greatsumini/react-facebook-login which is CJS-only and breaks
 * under Vite 8 / Rolldown's CJS-to-ESM interop (double-wrapped default
 * export → React error #130).
 *
 * API surface kept identical to the library's <FacebookLogin> so the
 * consuming code doesn't need to change.
 */
import { useEffect, useCallback } from 'react';

const SDK_URL = 'https://connect.facebook.net/en_US/sdk.js';
const SDK_SCRIPT_ID = 'facebook-jssdk';

/** Load the Facebook JS SDK once. Resolves when `window.FB` is ready. */
function loadFBSdk() {
  return new Promise((resolve, reject) => {
    if (window.FB) {
      resolve(window.FB);
      return;
    }
    if (document.getElementById(SDK_SCRIPT_ID)) {
      // Script tag exists but FB not ready yet — wait for it
      const prev = window.fbAsyncInit;
      window.fbAsyncInit = () => {
        prev?.();
        resolve(window.FB);
      };
      return;
    }
    const script = document.createElement('script');
    script.id = SDK_SCRIPT_ID;
    script.src = SDK_URL;
    script.async = true;
    script.defer = true;
    script.onerror = () => reject(new Error('Failed to load Facebook SDK'));
    const prev = window.fbAsyncInit;
    window.fbAsyncInit = () => {
      prev?.();
      resolve(window.FB);
    };
    document.body.appendChild(script);
  });
}

export default function FacebookLogin({
  appId,
  language = 'en_US',
  scope = 'public_profile, email',
  fields = 'name,email,picture',
  onSuccess,
  onFail,
  onProfileSuccess,
  className,
  style,
  children = 'Login with Facebook',
  render,
  autoLoad = false,
}) {
  useEffect(() => {
    loadFBSdk()
      .then((FB) => {
        FB.init({ appId, cookie: true, xfbml: false, version: 'v16.0' });
      })
      .catch((err) => {
        onFail?.({ status: 'facebookNotLoaded', message: err.message });
      });
  }, [appId]);

  const handleClick = useCallback(() => {
    if (!window.FB) {
      onFail?.({ status: 'facebookNotLoaded', message: 'Facebook SDK not loaded' });
      return;
    }

    // Clear any previous Facebook login session so the auth dialog always
    // shows a fresh login prompt instead of reusing stale trial data.
    const doLogin = () => {
      window.FB.login(
        (response) => {
          if (!response.authResponse) {
            onFail?.({ status: 'loginCancelled' });
            return;
          }
          onSuccess?.(response.authResponse);
          if (onProfileSuccess) {
            window.FB.api('/me', { fields }, onProfileSuccess);
          }
        },
        {
          scope,
          auth_type: 'rerequest',    // Force the authorization dialog (not the FB home page)
          return_scopes: true,
        },
      );
    };

    // Logout first to clear any cached session / stale trial data
    window.FB.getLoginStatus((statusResponse) => {
      if (statusResponse.status === 'connected') {
        window.FB.logout(() => doLogin());
      } else {
        doLogin();
      }
    });
  }, [scope, fields, onSuccess, onFail, onProfileSuccess]);

  if (render) {
    return render({ onClick: handleClick });
  }

  return (
    <button type="button" onClick={handleClick} className={className} style={style}>
      {children}
    </button>
  );
}
